/**
 * Shared prose styling for every TipTap surface that renders brief content
 * (live section editor, Library artifact preview, template preview panel).
 *
 * Tailwind v4 preflight strips `font-size`/`font-weight` from h1-h6 and
 * `list-style` from ul/ol; without these overrides heading/list nodes are
 * visually indistinguishable from paragraphs. The `font-synthesis` override
 * is required because global.css disables synthesis and the "Helvetica Now
 * for Monks" @font-face only ships upright variants (no italic file) —
 * without enabling synthesis, `<em>` renders identical to normal text.
 * `@tailwindcss/typography` is not installed so `prose` would silently no-op.
 *
 * Spacing rules are structural, not authored: the brief-generation agent
 * emits fragmented lists (one `<ul>` per group, sometimes separated by stray
 * empty paragraphs), so vertical rhythm must come from the document shape —
 * a list item that contains a nested sub-list reads as a "group" and gets
 * air before and after, independent of whether the agent emitted blank
 * lines around it.
 *
 * Surfaces append their own `[&_.ProseMirror]:min-h-*` since the editable
 * editor needs a taller click target than the read-only previews.
 */
export const BRIEF_PROSE_CLASSES = [
  'text-foreground text-sm',
  '[&_.ProseMirror]:outline-none [&_.ProseMirror]:[font-synthesis:style_weight]',
  '[&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-semibold [&_.ProseMirror_h1]:my-3',
  '[&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:my-2',
  '[&_.ProseMirror_h3]:text-lg [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:my-2',
  '[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6',
  '[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6',
  '[&_.ProseMirror_li]:my-1',
  '[&_.ProseMirror_em]:italic',
  '[&_.ProseMirror_strong]:font-semibold',
  '[&_.ProseMirror_p]:my-1',
  // A list item that contains a sub-list is a visual group: air before + after.
  // (`:has()` outweighs the plain `li` my-1 above by specificity, not order.)
  '[&_.ProseMirror_li:has(>ul)]:my-5 [&_.ProseMirror_li:has(>ol)]:my-5',
  // Sub-list sits slightly off its label line.
  '[&_.ProseMirror_li>ul]:mt-2 [&_.ProseMirror_li>ol]:mt-2',
  // Top-level paragraph-then-list and list-then-paragraph transitions.
  '[&_.ProseMirror>p+ul]:mt-1.5 [&_.ProseMirror>p+ol]:mt-1.5',
  '[&_.ProseMirror>ul+p]:mt-2.5 [&_.ProseMirror>ol+p]:mt-2.5',
  // Baseline rhythm between top-level blocks even when no empty paragraph
  // separates two adjacent lists.
  '[&_.ProseMirror>ul]:my-2 [&_.ProseMirror>ol]:my-2',
].join(' ');

/**
 * Section titles render at heading-md so a brief reads with the visual
 * hierarchy of a document rather than a form, while keeping the underline
 * divider that separates each section. Foreground (not accent) color per
 * design review. The title is a DTO label (`section.name`), never part of
 * the TipTap doc.
 */
export const BRIEF_SECTION_TITLE_CLASSES =
  'text-heading-md font-semibold text-foreground border-b border-border-strong pb-2 mb-2';
